// src/app/resources/page.tsx - Fixed version
'use client';

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Button,
  Skeleton,
  Pagination,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  FilterList,
  Download as DownloadIcon,
  OpenInNew,
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  School as HandbookIcon,
  Assignment as FormIcon,
  Info as GuidelinesIcon,
  Folder as FolderIcon,
  CalendarToday,
  Visibility,
  GetApp,
} from '@mui/icons-material';

// Import types - temporary mock hook until you add the real one
import { getMediaUrl, formatDate } from '@/lib/api';

// Temporary mock hook - replace this with your real useResources hook
const useResources = () => {
  // This will be replaced with your real API call
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [resources, setResources] = React.useState<Resource[]>([]);

  React.useEffect(() => {
    // Simulate API call
    const fetchResources = async () => {
      try {
        setLoading(true);
        const response = await fetch('http://localhost:1337/api/resources?populate=*');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Resources API response:', data);
        
        if (data.data && Array.isArray(data.data)) {
          setResources(data.data);
        } else {
          setResources([]);
        }
        setError(null);
      } catch (err) {
        console.error('Failed to fetch resources:', err);
        setError(err instanceof Error ? err.message : 'Failed to load resources');
        setResources([]);
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  return { resources, loading, error };
};

// Resource and ResourceType interfaces - add these to your types/index.ts
interface Resource {
  id: number;
  title: string;
  description?: string;
  resourceType: string;
  externalLink?: string;
  category?: string;
  accessLevel?: string;
  version?: string;
  lastUpdated?: string;
  fileSize?: string;
  slug?: string;
  relatedThemes?: any[];
  file?: {
    url: string;
    name: string;
    ext: string;
    mime: string;
  } | null;
}

// =====================
// STYLED COMPONENTS
// =====================

const HeroSection = styled(Box)(() => ({
  minHeight: '40vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  background: 'linear-gradient(135deg, #242424 0%, #1a1a1a 100%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("/api/placeholder/1920/600") center/cover',
    opacity: 0.2,
    zIndex: 1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(36, 36, 36, 0.8) 0%, rgba(26, 26, 26, 0.9) 100%)',
    zIndex: 2,
  }
}));

const HeroContent = styled(Box)({
  position: 'relative',
  zIndex: 3,
  color: 'white',
  textAlign: 'center',
});

const FilterContainer = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 100%)',
  border: '1px solid #3a3a3a',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
}));

const ResourceCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
  border: '1px solid #444444',
  borderRadius: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  
  '&:hover': {
    borderColor: '#FFD400',
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(255, 212, 0, 0.2)',
  },
}));

const StatCard = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 100%)',
  border: '1px solid #444444',
  padding: theme.spacing(2),
  textAlign: 'center',
  minWidth: '120px',
  
  '&:hover': {
    borderColor: '#FFD400',
    transform: 'translateY(-2px)',
    transition: 'all 0.3s ease',
  },
}));

// =====================
// SUB-COMPONENTS
// =====================

interface ResourceItemProps {
  resource: Resource;
  onClick: (resource: Resource) => void;
}

const ResourceItem = React.memo<ResourceItemProps>(({ resource, onClick }) => {
  const handleClick = useCallback(() => {
    onClick(resource);
  }, [resource, onClick]);

  const getResourceIcon = (type: string, fileUrl?: string) => {
    if (fileUrl) {
      const extension = fileUrl.split('.').pop()?.toLowerCase();
      switch (extension) {
        case 'pdf': return <PdfIcon sx={{ color: '#f44336' }} />;
        case 'doc':
        case 'docx': return <DocumentIcon sx={{ color: '#2196f3' }} />;
        case 'xls':
        case 'xlsx': return <ExcelIcon sx={{ color: '#4caf50' }} />;
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif': return <ImageIcon sx={{ color: '#ff9800' }} />;
        default: return <FileIcon sx={{ color: '#9e9e9e' }} />;
      }
    }
    
    switch (type) {
      case 'Student Handbook': 
      case 'Handbook': return <HandbookIcon sx={{ color: '#2196f3' }} />;
      case 'Application Form': return <FormIcon sx={{ color: '#4caf50' }} />;
      case 'Guidelines': 
      case 'Guide': return <GuidelinesIcon sx={{ color: '#ff9800' }} />;
      default: return <DocumentIcon sx={{ color: '#9e9e9e' }} />;
    }
  };

  const handleDownload = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      if (resource.file?.url) {
        // Use the getMediaUrl helper to construct the proper URL
        const downloadUrl = getMediaUrl(resource.file);
        console.log('Downloading file from:', downloadUrl);
        window.open(downloadUrl, '_blank');
      } else if (resource.externalLink) {
        console.log('Opening external link:', resource.externalLink);
        window.open(resource.externalLink, '_blank', 'noopener,noreferrer');
      } else {
        console.log('No download link available for:', resource.title);
        // Show a message to the user that this resource is not downloadable
        alert('This resource is not available for download.');
      }
    } catch (error) {
      console.error('Error downloading resource:', error);
      alert('Error downloading file. Please try again later.');
    }
  }, [resource]);

  return (
    <ResourceCard onClick={handleClick}>
      <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Chip
            icon={getResourceIcon(resource.resourceType, resource.file?.url)}
            label={resource.resourceType}
            size="small"
            sx={{
              background: 'rgba(255, 212, 0, 0.2)',
              color: '#FFD400',
              border: '1px solid rgba(255, 212, 0, 0.3)',
              fontSize: '0.75rem',
            }}
          />
          
          {resource.category && (
            <Chip
              label={resource.category}
              size="small"
              sx={{
                background: 'rgba(255, 212, 0, 0.1)',
                color: '#FFD400',
                border: '1px solid rgba(255, 212, 0, 0.3)',
                fontSize: '0.7rem',
              }}
            />
          )}
        </Box>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            color: '#FFD400',
            fontWeight: 600,
            marginBottom: 1,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {resource.title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: '#e0e0e0',
            lineHeight: 1.6,
            marginBottom: 2,
            flexGrow: 1,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {resource.description}
        </Typography>

        {/* File Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {resource.version && (
            <Typography variant="caption" sx={{ color: '#888' }}>
              Version: {resource.version}
            </Typography>
          )}
          
          {resource.lastUpdated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: '0.8rem', color: '#888' }} />
              <Typography variant="caption" sx={{ color: '#888' }}>
                Updated: {formatDate(resource.lastUpdated)}
              </Typography>
            </Box>
          )}

          {resource.fileSize && (
            <Typography variant="caption" sx={{ color: '#888' }}>
              Size: {resource.fileSize}
            </Typography>
          )}
        </Box>

        {/* Access Level */}
        {resource.accessLevel && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={resource.accessLevel}
              size="small"
              sx={{
                background: resource.accessLevel === 'Public' 
                  ? 'rgba(76, 175, 80, 0.2)' 
                  : 'rgba(255, 152, 0, 0.2)',
                color: resource.accessLevel === 'Public' ? '#66bb6a' : '#ffa726',
                border: `1px solid ${resource.accessLevel === 'Public' ? '#66bb6a' : '#ffa726'}`,
                fontSize: '0.7rem',
              }}
            />
          </Box>
        )}

        {/* Action Button */}
        <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid rgba(255, 212, 0, 0.2)' }}>
          {(resource.file?.url || resource.externalLink) ? (
            <Button
              variant="contained"
              size="small"
              fullWidth
              startIcon={resource.file?.url ? <DownloadIcon /> : <OpenInNew />}
              onClick={handleDownload}
              sx={{
                background: 'linear-gradient(45deg, #FFD400, #e6c000)',
                color: '#242424',
                fontWeight: 600,
                fontSize: '0.8rem',
                '&:hover': {
                  background: 'linear-gradient(45deg, #e6c000, #FFD400)',
                }
              }}
            >
              {resource.file?.url ? 'Download' : 'View'}
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="small"
              fullWidth
              disabled
              sx={{
                color: '#666',
                borderColor: '#666',
                fontSize: '0.8rem',
              }}
            >
              Not Available
            </Button>
          )}
        </Box>
      </CardContent>
    </ResourceCard>
  );
});

ResourceItem.displayName = 'ResourceItem';

const LoadingSkeleton = React.memo(() => (
  <>
    {Array.from({ length: 6 }, (_, index) => (
      <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)', 
          border: '1px solid #444444',
          height: 400,
        }}>
          <CardContent sx={{ p: 3 }}>
            <Skeleton variant="rectangular" height={32} width="45%" sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
            <Skeleton variant="text" height={28} width="85%" sx={{ bgcolor: 'rgba(255, 255, 255, 0.15)', mb: 1 }} />
            <Skeleton variant="text" height={16} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 1 }} />
            <Skeleton variant="text" height={16} width="70%" sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
            <Skeleton variant="rectangular" height={36} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </>
));

LoadingSkeleton.displayName = 'LoadingSkeleton';

// =====================
// MAIN COMPONENT
// =====================

const ResourcesPage: React.FC = () => {
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  // API data - replace mock with real API
  const { resources: allResources, loading, error } = useResources();

  // Remove the mock data since we're now using real API
  // const allResources: Resource[] = [...]

  // Get unique categories for filter
  const categories = useMemo(() => {
    const cats = allResources.map(resource => resource.category).filter(Boolean);
    return [...new Set(cats)];
  }, [allResources]);

  // Filter logic
  const filteredResources = useMemo(() => {
    return allResources.filter(resource => {
      const matchesSearch = searchQuery === '' || 
        resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesType = selectedType === '' || resource.resourceType === selectedType;
      const matchesCategory = selectedCategory === '' || resource.category === selectedCategory;
      
      return matchesSearch && matchesType && matchesCategory;
    });
  }, [allResources, searchQuery, selectedType, selectedCategory]);

  // Paginated data
  const paginatedResources = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredResources.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredResources, currentPage]);

  const totalPages = Math.ceil(filteredResources.length / itemsPerPage);

  // Event handlers
  const handleResourceClick = useCallback((resource: Resource) => {
    console.log('Resource clicked:', resource.title);
    
    // Handle the click based on what's available
    if (resource.file?.url) {
      const downloadUrl = getMediaUrl(resource.file);
      console.log('Opening file from:', downloadUrl);
      window.open(downloadUrl, '_blank');
    } else if (resource.externalLink) {
      console.log('Opening external link:', resource.externalLink);
      window.open(resource.externalLink, '_blank', 'noopener,noreferrer');
    } else {
      console.log('No link available for:', resource.title);
      // Could navigate to a detail page instead
      // router.push(`/resources/${resource.slug || resource.id}`);
    }
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', background: '#1a1a1a' }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <HeroContent>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                marginBottom: 2,
                background: 'linear-gradient(45deg, #FFD400, #ffffff)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Resources
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                fontWeight: 400,
                color: '#e0e0e0',
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.5,
              }}
            >
              Access program materials, handbooks, application forms, and collaboration guidelines
            </Typography>
          </HeroContent>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Statistics */}
        <Box sx={{ display: 'flex', gap: 3, mb: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
          <StatCard>
            <FolderIcon sx={{ color: '#FFD400', fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: '#FFD400', fontWeight: 700 }}>
              {allResources.length}
            </Typography>
            <Typography variant="caption" sx={{ color: '#b0b0b0' }}>
              Total Resources
            </Typography>
          </StatCard>
          
          <StatCard>
            <Visibility sx={{ color: '#FFD400', fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: '#FFD400', fontWeight: 700 }}>
              {allResources.filter(r => r.accessLevel === 'Public').length}
            </Typography>
            <Typography variant="caption" sx={{ color: '#b0b0b0' }}>
              Public Access
            </Typography>
          </StatCard>
          
          <StatCard>
            <GetApp sx={{ color: '#FFD400', fontSize: '2rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: '#FFD400', fontWeight: 700 }}>
              {new Set(allResources.map(r => r.resourceType)).size}
            </Typography>
            <Typography variant="caption" sx={{ color: '#b0b0b0' }}>
              Resource Types
            </Typography>
          </StatCard>
        </Box>

        {/* Filters */}
        <FilterContainer>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#FFD400' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#e0e0e0',
                    '& fieldset': { borderColor: '#444444' },
                    '&:hover fieldset': { borderColor: '#FFD400' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD400' },
                  },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#b0b0b0' }}>Resource Type</InputLabel>
                <Select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  label="Resource Type"
                  sx={{
                    color: '#e0e0e0',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444444' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFD400' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFD400' },
                  }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="Student Handbook">Student Handbooks</MenuItem>
                  <MenuItem value="Application Form">Application Forms</MenuItem>
                  <MenuItem value="Guidelines">Guidelines</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#b0b0b0' }}>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Category"
                  sx={{
                    color: '#e0e0e0',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444444' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFD400' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFD400' },
                  }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setSearchQuery('');
                  setSelectedType('');
                  setSelectedCategory('');
                  setCurrentPage(1);
                }}
                sx={{
                  color: '#FFD400',
                  borderColor: '#FFD400',
                  py: 1.5,
                  '&:hover': {
                    background: 'rgba(255, 212, 0, 0.1)',
                    borderColor: '#FFD400',
                  }
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </FilterContainer>

        {/* Content */}
        {loading ? (
          <Grid container spacing={3}>
            <LoadingSkeleton />
          </Grid>
        ) : error ? (
          <Paper sx={{ p: 4, textAlign: 'center', background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)', border: '1px solid #444444' }}>
            <Typography variant="h6" sx={{ color: '#e0e0e0', mb: 2 }}>Unable to Load Resources</Typography>
            <Typography variant="body2" sx={{ color: '#b0b0b0' }}>{error}</Typography>
          </Paper>
        ) : paginatedResources.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {paginatedResources.map((resource) => (
                <Grid key={resource.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <ResourceItem resource={resource} onClick={handleResourceClick} />
                </Grid>
              ))}
            </Grid>
            
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, page) => setCurrentPage(page)}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: '#e0e0e0',
                      borderColor: '#444444',
                      '&:hover': { backgroundColor: 'rgba(255, 212, 0, 0.1)', borderColor: '#FFD400' },
                      '&.Mui-selected': { backgroundColor: '#FFD400', color: '#242424' },
                    },
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center', background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)', border: '1px solid #444444' }}>
            <Typography variant="h6" sx={{ color: '#e0e0e0', mb: 1 }}>No Resources Found</Typography>
            <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
              {searchQuery || selectedType || selectedCategory
                ? 'Try adjusting your search criteria or clearing filters.'
                : 'Resources will appear here once they are uploaded to the system.'}
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default ResourcesPage;